/**
 * StepWizard — Multi-step form wizard with progress indicator
 * Used across: Compliance setup, Connection wizard, Import wizard, Agent create
 */

import { useState, ReactNode } from 'react';
import { Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface WizardStep {
  id: string;
  title: string;
  description?: string;
  content: ReactNode;
  /** Validation function — return true to allow proceeding */
  canProceed?: () => boolean;
}

interface StepWizardProps {
  steps: WizardStep[];
  onComplete: () => void;
  onCancel?: () => void;
  completeLabel?: string;
  className?: string;
}

export function StepWizard({ steps, onComplete, onCancel, completeLabel = 'Complete', className }: StepWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const isLastStep = currentStep === steps.length - 1;
  const step = steps[currentStep]!;

  const goNext = () => {
    if (step.canProceed && !step.canProceed()) return;
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const goToStep = (index: number) => {
    // Can only go to completed steps or the current+1 step
    if (index <= currentStep || completedSteps.has(index - 1)) {
      setCurrentStep(index);
    }
  };

  // Resolve canProceed once per render so the disabled state on the
  // Next/Done button reflects current form state without users having
  // to click and silently fail.
  const canProceedNow = step.canProceed ? step.canProceed() : true;

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Step indicator */}
      <div className="flex items-center gap-1 px-6 py-4 border-b border-border/40 overflow-x-auto shrink-0">
        {steps.map((s, i) => {
          const isCompleted = completedSteps.has(i);
          const isCurrent = i === currentStep;

          return (
            <div key={s.id} className="flex items-center">
              <button
                onClick={() => goToStep(i)}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors whitespace-nowrap',
                  isCurrent && 'bg-primary/10 text-primary font-medium',
                  isCompleted && !isCurrent && 'text-success cursor-pointer hover:bg-muted/50',
                  !isCurrent && !isCompleted && 'text-muted-foreground',
                )}
                disabled={!isCompleted && i > currentStep}
              >
                <span className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium border',
                  isCurrent && 'border-primary bg-primary text-primary-foreground',
                  isCompleted && !isCurrent && 'border-success bg-success text-white',
                  !isCurrent && !isCompleted && 'border-border text-muted-foreground',
                )}>
                  {isCompleted ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                {s.title}
              </button>
              {i < steps.length - 1 && (
                <ChevronRight className="h-4 w-4 text-muted-foreground/30 mx-1 shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-auto px-6 py-6">
        {step.description && (
          <p className="text-sm text-muted-foreground mb-4">{step.description}</p>
        )}
        {step.content}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-6 py-3 border-t border-border/40 shrink-0">
        <div>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {currentStep > 0 && (
            <Button variant="outline" onClick={goBack} className="gap-1.5">
              <ChevronLeft className="h-4 w-4" /> Back
            </Button>
          )}
          <Button
            onClick={goNext}
            disabled={!canProceedNow}
            title={
              !canProceedNow
                ? 'Complete the required fields above before continuing'
                : undefined
            }
          >
            {isLastStep ? completeLabel : 'Next'}
            {!isLastStep && <ChevronRight className="h-4 w-4 ml-1.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
